/**
 * Регулярное выражение для поиска атрибута width в HTML/коде вставки
 * Находит width="123", width: "123", width='123', width:123 и т.д.
 */
const WIDTH_ATTRIBUTE_PATTERN = /width[=:"]\s*["']?(\d+)["']?/i;

/**
 * Регулярное выражение для поиска атрибута height в HTML/коде вставки
 * Находит height="123", height: "123", height='123', height:123 и т.д.
 */
const HEIGHT_ATTRIBUTE_PATTERN = /height[=:"]\s*["']?(\d+)["']?/i;

/**
 * Регулярное выражение для замены атрибута width (глобальная, без учета регистра)
 */
const WIDTH_ATTRIBUTE_REPLACE_PATTERN = /width[=:"]\s*["']?\d+["']?/gi;

/**
 * Регулярное выражение для замены атрибута height (глобальная, без учета регистра)
 */
const HEIGHT_ATTRIBUTE_REPLACE_PATTERN = /height[=:"]\s*["']?\d+["']?/gi;

/**
 * Регулярное выражение для поиска последовательности цифр
 */
const DIGITS_PATTERN = /\d+/;

/**
 * Корректирует размеры iframe в кодах вставки на основе реальных пропорций видео
 * @param codes - объект с кодами вставки разных типов
 * @param videoWidth - реальная ширина видео
 * @param videoHeight - реальная высота видео
 * @returns объект с откорректированными кодами вставки
 */
export function adjustIframeSizes(
    codes: Record<string, string>,
    videoWidth: number,
    videoHeight: number,
): Record<string, string> {
    const realAspectRatio = videoWidth / videoHeight;

    const adjustedCodes: Record<string, string> = { ...codes };

    for (const key in adjustedCodes) {
        const code = adjustedCodes[key] as string;

        const widthMatch = code.match(WIDTH_ATTRIBUTE_PATTERN);
        const heightMatch = code.match(HEIGHT_ATTRIBUTE_PATTERN);

        if (!widthMatch || !heightMatch) {
            continue;
        }

        const currentWidth = parseInt(widthMatch[1], 10);
        const currentHeight = parseInt(heightMatch[1], 10);
        const currentAspectRatio = currentWidth / currentHeight;

        const threshold = 0.01;
        if (Math.abs(currentAspectRatio - realAspectRatio) < threshold) {
            continue;
        }

        // Вычисляем новые размеры с сохранением площади
        // Площадь: area = width * height
        // Для сохранения площади: newWidth * newHeight = currentWidth * currentHeight
        // При этом: newWidth / newHeight = realAspectRatio
        // Отсюда: newWidth = sqrt(area * aspectRatio), newHeight = sqrt(area / aspectRatio)
        const area = currentWidth * currentHeight;
        const newWidth = Math.round(Math.sqrt(area * realAspectRatio));
        const newHeight = Math.round(Math.sqrt(area / realAspectRatio));

        let adjustedCode = code;

        adjustedCode = adjustedCode.replace(WIDTH_ATTRIBUTE_REPLACE_PATTERN, (match) => {
            if (match.includes('"')) {
                return match.replace(DIGITS_PATTERN, newWidth.toString());
            } else if (match.includes("'")) {
                return match.replace(DIGITS_PATTERN, newWidth.toString());
            }
            return match.replace(DIGITS_PATTERN, newWidth.toString());
        });

        adjustedCode = adjustedCode.replace(HEIGHT_ATTRIBUTE_REPLACE_PATTERN, (match) => {
            if (match.includes('"')) {
                return match.replace(DIGITS_PATTERN, newHeight.toString());
            } else if (match.includes("'")) {
                return match.replace(DIGITS_PATTERN, newHeight.toString());
            }
            return match.replace(DIGITS_PATTERN, newHeight.toString());
        });

        adjustedCodes[key] = adjustedCode;
    }

    return adjustedCodes;
}
